import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Box,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config/config';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    margin: theme.spacing(2, 0),
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  submitButton: {
    marginTop: theme.spacing(2),
    backgroundColor: '#1976d2',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1565c0',
    },
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
}));

const api = axios.create({
  baseURL: config.API.BASE_URL,
  timeout: config.API.TIMEOUT,
});

const SettingPage = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    welcomeMessage: '',
    privacyPolicyLink: '',
    privacyPolicyText: '',
    termAndCondition: '',
    isAppActive: true,
    coinPerDollar: 50,
    withdrawLimit: 0,
    chargeForRandomCall: 0,
    chargeForPrivateCall: 0,
    redirectAppUrl: '',
    redirectMessage: '',
    googlePlaySwitch: false,
    stripeSwitch: false,
    razorPaySwitch: false,
    isFake: false,
    isData: false,
    agoraKey: '',
    agoraCertificate: '',
    googlePlayEmail: '',
    googlePlayKey: '',
    stripePublishableKey: '',
    stripeSecretKey: '',
    razorPayId: '',
    razorSecretKey: '',
    coinCharge: 0,
    paymentGateway: '',
    link: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await api.get('/setting');
      if (response.data.status) {
        setSettings(response.data.setting);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Error loading settings');
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.defaults.headers.common['key'] = config.SECURITY.SECRET_KEY;

      const response = await api.patch('/setting', settings);
      if (response.data.status) {
        toast.success('Settings updated successfully!');
      } else {
        toast.error(response.data.message || 'Error updating settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Error updating settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Application Settings
      </Typography>

      <form onSubmit={handleSubmit}>
        {/* General Settings */}
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            General Configuration
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Welcome Message"
                value={settings.welcomeMessage || ''}
                onChange={handleInputChange('welcomeMessage')}
                multiline
                rows={3}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Redirect App URL"
                value={settings.redirectAppUrl || ''}
                onChange={handleInputChange('redirectAppUrl')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Redirect Message"
                value={settings.redirectMessage || ''}
                onChange={handleInputChange('redirectMessage')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.isAppActive || false}
                    onChange={handleInputChange('isAppActive')}
                    color="primary"
                  />
                }
                label="App Active"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* App Policies */}
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            App Policies
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Privacy Policy Link"
                value={settings.privacyPolicyLink || ''}
                onChange={handleInputChange('privacyPolicyLink')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Privacy Policy Text"
                value={settings.privacyPolicyText || ''}
                onChange={handleInputChange('privacyPolicyText')}
                multiline
                rows={4}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Terms and Conditions"
                value={settings.termAndCondition || ''}
                onChange={handleInputChange('termAndCondition')}
                multiline
                rows={4}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Coin & Charges Configuration */}
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Coin & Charges Configuration
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Coin Per Dollar"
                type="number"
                value={settings.coinPerDollar || 50}
                onChange={handleInputChange('coinPerDollar')}
                variant="outlined"
                inputProps={{ min: 1 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Withdraw Limit"
                type="number"
                value={settings.withdrawLimit || 0}
                onChange={handleInputChange('withdrawLimit')}
                variant="outlined"
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Charge for Random Call"
                type="number"
                value={settings.chargeForRandomCall || 0}
                onChange={handleInputChange('chargeForRandomCall')}
                variant="outlined"
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Charge for Private Call"
                type="number"
                value={settings.chargeForPrivateCall || 0}
                onChange={handleInputChange('chargeForPrivateCall')}
                variant="outlined"
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Payment Gateway Switches */}
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Payment Gateway Configuration
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.googlePlaySwitch || false}
                    onChange={handleInputChange('googlePlaySwitch')}
                    color="primary"
                  />
                }
                label="Google Play Switch"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.stripeSwitch || false}
                    onChange={handleInputChange('stripeSwitch')}
                    color="primary"
                  />
                }
                label="Stripe Switch"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.razorPaySwitch || false}
                    onChange={handleInputChange('razorPaySwitch')}
                    color="primary"
                  />
                }
                label="RazorPay Switch"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Advanced Configuration */}
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Advanced Configuration
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.isFake || false}
                    onChange={handleInputChange('isFake')}
                    color="primary"
                  />
                }
                label="Is Fake"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.isData || false}
                    onChange={handleInputChange('isData')}
                    color="primary"
                  />
                }
                label="Is Data"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* API Keys Configuration */}
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            API Keys & Credentials
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Agora Key"
                value={settings.agoraKey || ''}
                onChange={handleInputChange('agoraKey')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Agora Certificate"
                value={settings.agoraCertificate || ''}
                onChange={handleInputChange('agoraCertificate')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Google Play Email"
                value={settings.googlePlayEmail || ''}
                onChange={handleInputChange('googlePlayEmail')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Google Play Key"
                value={settings.googlePlayKey || ''}
                onChange={handleInputChange('googlePlayKey')}
                variant="outlined"
                type="password"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stripe Publishable Key"
                value={settings.stripePublishableKey || ''}
                onChange={handleInputChange('stripePublishableKey')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stripe Secret Key"
                value={settings.stripeSecretKey || ''}
                onChange={handleInputChange('stripeSecretKey')}
                variant="outlined"
                type="password"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RazorPay ID"
                value={settings.razorPayId || ''}
                onChange={handleInputChange('razorPayId')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RazorPay Secret Key"
                value={settings.razorSecretKey || ''}
                onChange={handleInputChange('razorSecretKey')}
                variant="outlined"
                type="password"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Coin Charge"
                type="number"
                value={settings.coinCharge || 0}
                onChange={handleInputChange('coinCharge')}
                variant="outlined"
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Link"
                value={settings.link || ''}
                onChange={handleInputChange('link')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Gateway (JSON)"
                value={settings.paymentGateway || ''}
                onChange={handleInputChange('paymentGateway')}
                variant="outlined"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Paper>

        <Box mt={3}>
          <Button
            type="submit"
            variant="contained"
            className={classes.submitButton}
            disabled={loading}
            size="large"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </form>
    </div>
  );
};

export default SettingPage;